import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState, useRecoilCallback } from 'recoil';
import { useTranslation } from 'react-i18next';

// State atoms
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { atom_tourState, atom_routeToStopLocation, atom_isNavigationMode, atom_isDesktop } from '@app/shared/state/tour.atom';
import { atom_visitedLocations } from '@app/shared/state/visited.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_activeCTANotification, atom_activeMessageNotification, TourNotification } from '@app/shared/state/notification.atom';
import { atom_modalType, atom_showModalContent } from '@app/shared/state/show-content-modal.atom';
import {
	atom_guideRoute,
	atom_routeTrackerState,
	RouteTrackerState,
	GuideRoute,
} from '@app/shared/state/route-tracker.atom';

// Utils
import {
	calculateDistance,
	calculateDistancesToStops,
	findClosestStop,
	isWithinArrivalThreshold,
	isOffRoute,
	isOffRouteFromCoords,
	formatDistance,
	StopDistance,
	ARRIVAL_THRESHOLD_METERS,
	OFF_ROUTE_COOLDOWN_MS,
	SKIPPED_COOLDOWN_MS,
	AUTO_ADVANCE_DELAY_MS,
} from '@app/shared/utils/geo-utils';

// Accessibility
import { announce } from '@app/shared/components/accessibility';

// Types
import { Stop } from '@app/shared/models/tour.type';

// ============ HELPER TYPES ============

export interface StopInfo {
	stopId: string;
	index: number;
	title: string;
	distance: number | null;
	visited: boolean;
	skipped: boolean;
	coords: { lat: number; lng: number };
}

// ============ DEFAULT TRACKER STATE ============

const DEFAULT_TRACKER_STATE: RouteTrackerState = {
	isActive: false,
	routeType: null,
	targetStopId: null,
	distanceToTarget: null,
	currentStopIndex: 0,
	closestStopIndex: null,
	skippedStopIds: [],
	status: 'tracking',
	lastArrivalNotified: null,
	lastOffRouteNotified: null,
	lastSkippedNotified: null,
	currentGuideStepIndex: 0,
};

// ============ HOOK ============

export function useRouteTracker() {
	const { t } = useTranslation();

	// ---- Read atoms (no geolocation subscription - read on-demand) ----
	const tourState = useRecoilValue(atom_tourState);
	const routeToStop = useRecoilValue(atom_routeToStopLocation);
	const guideRoute = useRecoilValue(atom_guideRoute);

	const [visitedLocations, setVisitedLocations] = useRecoilState(atom_visitedLocations);
	// Unified with UI nav-mode so tracking + panel visibility stay in sync.
	const [navigatorEnabled, setNavigatorEnabled] = useRecoilState(atom_isNavigationMode);
	const [trackerState, setTrackerState] = useRecoilState(atom_routeTrackerState);

	// On-demand geolocation read - no subscription, no re-renders on GPS ticks
	const getGeolocation = useRecoilCallback(({ snapshot }) => () =>
		snapshot.getLoadable(atom_geolocationState).getValue()
	, []);
	const setActiveCTANotification = useSetRecoilState(atom_activeCTANotification);
	const setActiveMessageNotification = useSetRecoilState(atom_activeMessageNotification);
	const setActiveStopState = useSetRecoilState(atom_activeStopState);

	// Desktop modal control — needed because nav-start CTA bypasses useNotificationTrigger.
	// Read isDesktop on-demand so this hook doesn't re-render on layout flips.
	const getIsDesktop = useRecoilCallback(({ snapshot }) =>
		() => snapshot.getLoadable(atom_isDesktop).getValue()
	, []);
	const setModalType = useSetRecoilState(atom_modalType);
	const setShowModalContent = useSetRecoilState(atom_showModalContent);

	// ---- Refs for stale closure prevention ----
	const trackerStateRef = useRef<RouteTrackerState>(trackerState);
	const visitedLocationsRef = useRef<Record<string, boolean>>(visitedLocations);
	const guideRouteRef = useRef<GuideRoute | null>(guideRoute);
	const navigatorEnabledRef = useRef(navigatorEnabled);
	const announcedSkippedRef = useRef<Set<string>>(new Set());
	const navCTASeenRef = useRef(false);
	const proximityToastedRef = useRef<Set<string>>(new Set());

	// Keep refs in sync
	useEffect(() => { trackerStateRef.current = trackerState; }, [trackerState]);
	useEffect(() => { navigatorEnabledRef.current = navigatorEnabled; }, [navigatorEnabled]);
	useEffect(() => { visitedLocationsRef.current = visitedLocations; }, [visitedLocations]);
	useEffect(() => { guideRouteRef.current = guideRoute; }, [guideRoute]);

	// ---- Helpers ----

	const getActiveStopOrder = useCallback((): string[] | null => {
		if (guideRoute?.stopOrder && guideRoute.stopOrder.length > 0) {
			return guideRoute.stopOrder;
		}
		// Fall back to tour stop order
		if (tourState?.stops && tourState.stops.length > 0) {
			return tourState.stops
				.slice()
				.sort((a, b) => (a.order ?? a.stop_index) - (b.order ?? b.stop_index))
				.map((s) => s.id);
		}
		return null;
	}, [guideRoute, tourState]);

	const determineRouteType = useCallback((): 'single-stop' | 'multi-stop' | 'guide-route' | null => {
		// Priority: single-stop (active navigation) > guide-route (tour path loaded) > null
		// Multi-stop reserved for future plannedRoute feature
		if (routeToStop) {
			return 'single-stop';
		}
		if (guideRoute?.stopOrder && guideRoute.stopOrder.length > 0 && guideRoute.segments.length > 0) {
			return 'guide-route';
		}
		return null;
	}, [guideRoute, routeToStop]);

	const getSingleStopTargetId = useCallback((): string | null => {
		if (!routeToStop || !tourState?.stops) return null;
		// The route's stopTitle matches a stop
		if (routeToStop.stopTitle) {
			const found = tourState.stops.find((s) => s.s_title === routeToStop.stopTitle);
			if (found) return found.id;
		}
		// Fall back: find the stop closest to the route destination
		const lastLeg = routeToStop.legs?.[routeToStop.legs.length - 1];
		if (lastLeg?.end_location) {
			const destLat = lastLeg.end_location.lat;
			const destLng = lastLeg.end_location.lng;
			let closestStop: Stop | null = null;
			let closestDist = Infinity;
			for (const stop of tourState.stops) {
				const dist = calculateDistance(
					destLat, destLng,
					stop.stop_location.latitude, stop.stop_location.longitude
				);
				if (dist < closestDist) {
					closestDist = dist;
					closestStop = stop;
				}
			}
			if (closestStop && closestDist < 500) return closestStop.id;
		}
		return null;
	}, [routeToStop, tourState]);

	const getStopsWithCoords = useCallback((stopIds: string[]): Array<{ id: string; lat: number; lng: number }> => {
		if (!tourState?.stops) return [];
		return stopIds
			.map((id) => {
				const stop = tourState.stops.find((s) => s.id === id);
				if (!stop) return null;
				return {
					id: stop.id,
					lat: stop.stop_location.latitude,
					lng: stop.stop_location.longitude,
				};
			})
			.filter((s): s is { id: string; lat: number; lng: number } => s !== null);
	}, [tourState]);

	const getStopTitle = useCallback((stopId: string): string | null => {
		if (!tourState?.stops) return null;
		const stop = tourState.stops.find((s) => s.id === stopId);
		return stop?.s_title ?? null;
	}, [tourState]);

	const getStopInfo = useCallback((stopId: string, index: number): StopInfo | null => {
		if (!tourState?.stops) return null;
		const stop = tourState.stops.find((s) => s.id === stopId);
		if (!stop) return null;

		const geo = getGeolocation();
		let distance: number | null = null;
		if (geo.latitude !== null && geo.longitude !== null) {
			distance = calculateDistance(
				geo.latitude, geo.longitude,
				stop.stop_location.latitude, stop.stop_location.longitude
			);
		}

		return {
			stopId: stop.id,
			index,
			title: stop.s_title,
			distance,
			visited: !!visitedLocations[stop.id],
			skipped: trackerState.skippedStopIds.includes(stop.id),
			coords: { lat: stop.stop_location.latitude, lng: stop.stop_location.longitude },
		};
	}, [tourState, getGeolocation, visitedLocations, trackerState.skippedStopIds]);

	// ---- Notification handlers ----

	const vibrate = useCallback((pattern: number[]) => {
		if (navigator.vibrate) {
			navigator.vibrate(pattern);
		}
	}, []);

	const showProximityToast = useCallback((stopId: string, title: string) => {
		if (proximityToastedRef.current.has(stopId)) return;
		proximityToastedRef.current.add(stopId);

		setActiveMessageNotification({
			id: 'nav-proximity-' + stopId + '-' + Date.now(),
			tourId: tourState?.id ?? '',
			active: true,
			triggerType: 'location',
			triggerConfig: { stopId, location: null, radius: ARRIVAL_THRESHOLD_METERS },
			notificationType: 'message',
			displayOnce: true,
			showInList: false,
			title: t('navigator.stop_nearby', { title }),
			content: '',
			icon: 'location',
			thumbnail: null,
			notificationScale: 1,
			order: 0,
			priority: 'high',
			ctaConfig: null,
			userState: {
				seen: false,
				seenOnList: false,
				dismissed: false,
				ctaClicked: false,
				firstSeenAt: null,
			},
			createdAt: { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 },
			updatedAt: { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 },
		} as TourNotification);
	}, [t, tourState, setActiveMessageNotification]);

	const handleArrival = useCallback((stopId: string, title: string) => {
		const state = trackerStateRef.current;
		if (state.lastArrivalNotified === stopId) return;

		vibrate([200, 100, 200]);
		announce(t('navigator.arrived_at', { title }), 'assertive');
		showProximityToast(stopId, title);

		setTrackerState((prev) => ({
			...prev,
			status: 'arrived',
			lastArrivalNotified: stopId,
		}));

		// Auto-marking on arrival is disabled — stops are only marked visited when the user
		// taps "Finished" in StopDetails.
	}, [t, vibrate, showProximityToast, setTrackerState]);

	const handleMultiStopArrival = useCallback((stopId: string, index: number) => {
		const state = trackerStateRef.current;
		if (state.lastArrivalNotified === stopId) return;

		const title = getStopTitle(stopId) ?? `Stop #${index + 1}`;
		vibrate([200, 100, 200]);
		announce(t('navigator.arrived_at', { title }), 'assertive');
		showProximityToast(stopId, title);

		// Auto-marking on arrival is disabled — stops are only marked visited on manual "Finished".
		// The advance/completion checks below still treat the just-arrived stop as visited locally
		// to keep the multi-stop sequence moving forward.

		// Check if all stops completed
		const stopOrder = getActiveStopOrder();
		if (stopOrder) {
			const updatedVisited = { ...visitedLocationsRef.current, [stopId]: true };
			const allVisited = stopOrder.every((id) => updatedVisited[id]);

			if (allVisited) {
				vibrate([200, 100, 200, 100, 400]);
				announce(t('navigator.tour_completed'), 'assertive');

				setTrackerState((prev) => ({
					...prev,
					status: 'completed',
					lastArrivalNotified: stopId,
				}));
				return;
			}
		}

		// Auto-advance to next unvisited stop. Skip undefined slots — stopOrder can contain
		// holes if any source stop was missing an id.
		if (stopOrder) {
			const updatedVisited = { ...visitedLocationsRef.current, [stopId]: true };
			const nextUnvisitedIndex = stopOrder.findIndex(
				(id, i) => i > index && !!id && !updatedVisited[id]
			);
			if (nextUnvisitedIndex !== -1 && stopOrder[nextUnvisitedIndex]) {
				setTimeout(() => {
					setTrackerState((prev) => ({
						...prev,
						status: 'tracking',
						targetStopId: stopOrder[nextUnvisitedIndex],
						currentStopIndex: nextUnvisitedIndex,
						lastArrivalNotified: stopId,
					}));
				}, AUTO_ADVANCE_DELAY_MS);
			} else {
				setTrackerState((prev) => ({
					...prev,
					status: 'arrived',
					lastArrivalNotified: stopId,
				}));
			}
		} else {
			setTrackerState((prev) => ({
				...prev,
				status: 'arrived',
				lastArrivalNotified: stopId,
			}));
		}
	}, [t, vibrate, showProximityToast, getStopTitle, getActiveStopOrder, setTrackerState]);

	const handleGuideRouteArrival = useCallback((stopId: string, index: number) => {
		const state = trackerStateRef.current;
		if (state.lastArrivalNotified === stopId) return;

		const gr = guideRouteRef.current;
		const title = getStopTitle(stopId) ?? `Stop #${index + 1}`;
		vibrate([200, 100, 200]);
		announce(t('navigator.arrived_at', { title }), 'assertive');
		showProximityToast(stopId, title);

		// Auto-marking on arrival is disabled — stops are only marked visited on manual "Finished".
		// The advance/completion checks below still treat the just-arrived stop as visited locally
		// to keep the multi-stop sequence moving forward.

		// Check if all stops completed
		if (gr?.stopOrder) {
			const updatedVisited = { ...visitedLocationsRef.current, [stopId]: true };
			const allVisited = gr.stopOrder.every((id) => updatedVisited[id]);

			if (allVisited) {
				vibrate([200, 100, 200, 100, 400]);
				announce(t('navigator.tour_completed'), 'assertive');

				setTrackerState((prev) => ({
					...prev,
					status: 'completed',
					lastArrivalNotified: stopId,
				}));
				return;
			}

			// Auto-advance to next unvisited stop. Skip undefined slots — guideRoute.stopOrder
			// can contain holes if any source stop was missing an id.
			const updatedVisitedForAdvance = { ...visitedLocationsRef.current, [stopId]: true };
			const nextUnvisitedIndex = gr.stopOrder.findIndex(
				(id, i) => i > index && !!id && !updatedVisitedForAdvance[id]
			);
			if (nextUnvisitedIndex !== -1 && gr.stopOrder[nextUnvisitedIndex]) {
				setTimeout(() => {
					setTrackerState((prev) => ({
						...prev,
						status: 'tracking',
						targetStopId: gr.stopOrder[nextUnvisitedIndex],
						currentStopIndex: nextUnvisitedIndex,
						lastArrivalNotified: stopId,
					}));
				}, AUTO_ADVANCE_DELAY_MS);
			} else {
				setTrackerState((prev) => ({
					...prev,
					status: 'arrived',
					lastArrivalNotified: stopId,
				}));
			}
		} else {
			setTrackerState((prev) => ({
				...prev,
				status: 'arrived',
				lastArrivalNotified: stopId,
			}));
		}
	}, [t, vibrate, showProximityToast, getStopTitle, setTrackerState]);

	const handleOffRoute = useCallback(() => {
		const state = trackerStateRef.current;
		const now = Date.now();

		// 60-second cooldown
		if (state.lastOffRouteNotified && now - state.lastOffRouteNotified < OFF_ROUTE_COOLDOWN_MS) {
			return;
		}

		vibrate([200, 100, 200]);
		announce(t('navigator.off_route'), 'polite');

		setTrackerState((prev) => ({
			...prev,
			status: 'off-route',
			lastOffRouteNotified: now,
		}));

		// Reset status after 5 seconds
		setTimeout(() => {
			setTrackerState((prev) => {
				if (prev.status === 'off-route') {
					return { ...prev, status: 'tracking' };
				}
				return prev;
			});
		}, 5000);
	}, [t, vibrate, setTrackerState]);

	const handleSkippedStop = useCallback((stopId: string, index: number) => {
		const state = trackerStateRef.current;

		// Duplicate prevention via ref
		if (announcedSkippedRef.current.has(stopId)) return;

		// Duplicate prevention via state
		if (state.lastSkippedNotified === stopId) return;
		if (state.skippedStopIds.includes(stopId)) return;

		announcedSkippedRef.current.add(stopId);

		const title = getStopTitle(stopId) ?? `Stop #${index + 1}`;
		announce(t('navigator.skipped_stop', { title }), 'polite');

		setTrackerState((prev) => ({
			...prev,
			status: 'skipped-stop',
			skippedStopIds: [...prev.skippedStopIds, stopId],
			lastSkippedNotified: stopId,
		}));

		// Reset status after 5 seconds
		setTimeout(() => {
			setTrackerState((prev) => {
				if (prev.status === 'skipped-stop') {
					return { ...prev, status: 'tracking' };
				}
				return prev;
			});
		}, 5000);
	}, [t, getStopTitle, setTrackerState]);

	// ---- Actions ----

	const enableNavigator = useCallback(() => {
		const routeType = determineRouteType();
		if (!routeType) return;

		// Reset announced skipped set and proximity toasts
		announcedSkippedRef.current = new Set();
		proximityToastedRef.current = new Set();

		let targetStopId: string | null = null;
		let currentStopIndex = 0;

		if (routeType === 'single-stop') {
			targetStopId = getSingleStopTargetId();
		} else if (routeType === 'multi-stop') {
			const stopOrder = getActiveStopOrder();
			if (stopOrder && stopOrder.length > 0) {
				// Find first unvisited stop
				const firstUnvisited = stopOrder.findIndex((id) => !visitedLocations[id]);
				currentStopIndex = firstUnvisited >= 0 ? firstUnvisited : 0;
				targetStopId = stopOrder[currentStopIndex];
			}
		} else if (routeType === 'guide-route') {
			if (guideRoute?.stopOrder && guideRoute.stopOrder.length > 0) {
				// Find first unvisited stop
				const firstUnvisited = guideRoute.stopOrder.findIndex((id) => !visitedLocations[id]);
				currentStopIndex = firstUnvisited >= 0 ? firstUnvisited : 0;
				targetStopId = guideRoute.stopOrder[currentStopIndex];
			}
		}

		console.log('[enableNavigator] computed:', {
			routeType,
			targetStopId,
			currentStopIndex,
			guideRouteStopOrder: guideRoute?.stopOrder,
			guideRouteSegmentCount: guideRoute?.segments?.length,
			visitedKeys: Object.keys(visitedLocations).filter(k => visitedLocations[k]),
		});

		setNavigatorEnabled(true);
		setTrackerState({
			isActive: true,
			routeType,
			targetStopId,
			distanceToTarget: null,
			currentStopIndex,
			closestStopIndex: null,
			skippedStopIds: [],
			status: 'tracking',
			lastArrivalNotified: null,
			lastOffRouteNotified: null,
			lastSkippedNotified: null,
			currentGuideStepIndex: 0,
		});

		// Sync activeStopState to the navigation target so MapCard / list / details all reflect it.
		if (targetStopId) {
			const targetStop = tourState?.stops?.find((s) => s.id === targetStopId);
			if (targetStop) setActiveStopState(targetStop);
		}

		// CTA for guide-route: show OpeningStopBlock-style welcome for the first stop (once per session)
		// Mirrors features/map/opening-stop-block/OpeningStopBlock.tsx — title, smallDesc, image, before-start description, skip.
		// Skipped if the user is already within 50m of the first stop — pointless to ask
		// for directions to a place they're standing at.
		const FIRST_STOP_PROXIMITY_M = 50;
		if (routeType === 'guide-route' && !navCTASeenRef.current && guideRoute?.stopOrder && guideRoute.stopOrder.length > 0) {
			const allUnvisited = guideRoute.stopOrder.every((id) => !visitedLocations[id]);
			if (allUnvisited) {
				const firstStopId = guideRoute.stopOrder[0];
				const firstStop = tourState?.stops?.find((s) => s.id === firstStopId);

				const geo = getGeolocation();
				const firstLat = firstStop?.stop_location?.latitude;
				const firstLng = firstStop?.stop_location?.longitude;
				const userAlreadyAtFirstStop = !!(
					geo?.latitude && geo?.longitude && typeof firstLat === 'number' && typeof firstLng === 'number'
					&& calculateDistance(geo.latitude, geo.longitude, firstLat, firstLng) <= FIRST_STOP_PROXIMITY_M
				);

				if (firstStop && !userAlreadyAtFirstStop) {
					navCTASeenRef.current = true;
					const stopImage = firstStop.loc_pics?.[0]?.url?.split('&token=')?.[0] ?? null;
					const beforeStartDesc = t('on_tour.before_start_stop.description') as string;
					const content = firstStop.s_smallDesc
						? `${firstStop.s_smallDesc}<br/><br/>${beforeStartDesc}`
						: beforeStartDesc;

					setActiveCTANotification({
						id: 'nav-start-' + Date.now(),
						tourId: tourState?.id ?? '',
						active: true,
						triggerType: 'action',
						triggerConfig: { stopId: firstStopId, location: null, radius: 0 },
						notificationType: 'cta',
						displayOnce: true,
						showInList: false,
						title: firstStop.s_title,
						content,
						icon: 'compass',
						thumbnail: stopImage,
						notificationScale: 1,
						order: 0,
						priority: 'high',
						ctaConfig: {
							actionType: 'navigate',
							actionValue: firstStopId,
							primaryButtonText: t('on_tour.before_start_stop.take_me_there') as string,
							secondaryButtonText: t('on_tour.before_start_stop.skip_btn') as string,
						},
						userState: {
							seen: false,
							seenOnList: false,
							dismissed: false,
							ctaClicked: false,
							firstSeenAt: null,
						},
						createdAt: { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 },
						updatedAt: { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 },
					} as TourNotification);

					// On desktop, the CTA renders inside the modal (DesktopCTAContent).
					// useNotificationTrigger.processNotifications normally opens the modal,
					// but we wrote the CTA atom directly — so trigger the modal ourselves here.
					// Mobile reads atom_activeCTANotification inline via MobileCTAContent and needs nothing extra.
					if (getIsDesktop()) {
						setModalType('desktopCTA');
						setShowModalContent(true);
					}
				}
			}
		}
	}, [
		determineRouteType, getSingleStopTargetId, getActiveStopOrder,
		guideRoute, visitedLocations, tourState,
		setNavigatorEnabled, setTrackerState, setActiveCTANotification, t,
		getIsDesktop, setModalType, setShowModalContent, getGeolocation, setActiveStopState,
	]);

	const disableNavigator = useRecoilCallback(({ snapshot }) => () => {
		setNavigatorEnabled(false);
		setTrackerState(DEFAULT_TRACKER_STATE);
		announcedSkippedRef.current = new Set();
		proximityToastedRef.current = new Set();

		// Clear any navigator-created notifications, and (desktop only) close the host modal
		// if it was showing one. Don't touch the modal otherwise — another modal might be open.
		// Read current values via snapshot first; calling other setters inside a setter's
		// updater function violates Recoil's pure-updater rule.
		const currentCTA = snapshot.getLoadable(atom_activeCTANotification).contents as TourNotification | null;
		if (currentCTA?.id?.startsWith('nav-')) {
			setActiveCTANotification(null);
			if (getIsDesktop()) {
				setModalType(null);
				setShowModalContent(false);
			}
		}
		const currentMsg = snapshot.getLoadable(atom_activeMessageNotification).contents as TourNotification | null;
		if (currentMsg?.id?.startsWith('nav-')) {
			setActiveMessageNotification(null);
		}
	}, [setNavigatorEnabled, setTrackerState, setActiveCTANotification, setActiveMessageNotification,
		getIsDesktop, setModalType, setShowModalContent]);

	const toggleNavigator = useCallback(() => {
		if (navigatorEnabled) {
			disableNavigator();
		} else {
			enableNavigator();
		}
	}, [navigatorEnabled, enableNavigator, disableNavigator]);

	const resetTracker = useCallback(() => {
		setTrackerState(DEFAULT_TRACKER_STATE);
		announcedSkippedRef.current = new Set();
		proximityToastedRef.current = new Set();
	}, [setTrackerState]);

	// ---- Main tracking function - called by GeolocationProvider on GPS updates ----
	const checkRouteProgress = useCallback((location: { lat: number; lng: number }) => {
		if (!trackerStateRef.current.isActive || !navigatorEnabledRef.current) return;
		if (trackerStateRef.current.status === 'completed') return;

		const userLat = location.lat;
		const userLng = location.lng;

		const ts = trackerStateRef.current;
		// ---- Single-stop mode ----
		if (ts.routeType === 'single-stop') {
			const targetId = ts.targetStopId;
			if (!targetId || !tourState?.stops) return;

			const targetStop = tourState.stops.find((s) => s.id === targetId);
			if (!targetStop) return;

			const distance = calculateDistance(
				userLat, userLng,
				targetStop.stop_location.latitude, targetStop.stop_location.longitude
			);

			// Update distance
			setTrackerState((prev) => ({
				...prev,
				distanceToTarget: distance,
			}));

			// Arrival check
			if (isWithinArrivalThreshold(distance, ARRIVAL_THRESHOLD_METERS)) {
				handleArrival(targetId, targetStop.s_title);
				return;
			}

			// Off-route check (if route polyline available)
			if (routeToStop?.overview_polyline?.points) {
				if (isOffRoute(userLat, userLng, routeToStop.overview_polyline.points)) {
					handleOffRoute();
				}
			}
		}

		// ---- Multi-stop mode ----
		else if (ts.routeType === 'multi-stop') {
			const stopOrder = getActiveStopOrder();
			if (!stopOrder || stopOrder.length === 0) return;

			const stopsWithCoords = getStopsWithCoords(stopOrder);
			if (stopsWithCoords.length === 0) return;

			const distances = calculateDistancesToStops(userLat, userLng, stopsWithCoords);
			const closest = findClosestStop(distances);

			// Update closest stop index
			if (closest) {
				setTrackerState((prev) => ({
					...prev,
					closestStopIndex: closest.index,
				}));
			}

			// Update distance to current target
			const targetIdMulti = ts.targetStopId;
			if (targetIdMulti) {
				const targetDist = distances.find((d) => d.stopId === targetIdMulti);
				if (targetDist) {
					setTrackerState((prev) => ({
						...prev,
						distanceToTarget: targetDist.distance,
					}));
				}
			}

			// Check arrival at any unvisited stop
			for (const dist of distances) {
				if (visitedLocationsRef.current[dist.stopId]) continue;

				if (isWithinArrivalThreshold(dist.distance, ARRIVAL_THRESHOLD_METERS)) {
					handleMultiStopArrival(dist.stopId, dist.index);
					return;
				}
			}

			// Check for skipped stops: if user is past a stop they haven't visited
			const currentIdx = ts.currentStopIndex;
			if (closest && closest.index > currentIdx) {
				for (let i = currentIdx; i < closest.index; i++) {
					const skippedId = stopOrder[i];
					if (skippedId && !visitedLocationsRef.current[skippedId]) {
						handleSkippedStop(skippedId, i);
					}
				}
			}

			// Off-route check per segment (if route polyline available)
			if (routeToStop?.overview_polyline?.points) {
				if (isOffRoute(userLat, userLng, routeToStop.overview_polyline.points)) {
					handleOffRoute();
				}
			}
		}

		// ---- Guide-route mode ----
		else if (ts.routeType === 'guide-route') {
			const gr = guideRouteRef.current;
			if (!gr?.stopOrder || gr.stopOrder.length === 0) return;

			const stopsWithCoords = getStopsWithCoords(gr.stopOrder);
			if (stopsWithCoords.length === 0) return;

			const distances = calculateDistancesToStops(userLat, userLng, stopsWithCoords);
			const closest = findClosestStop(distances);

			// Update closest stop index
			if (closest) {
				setTrackerState((prev) => ({
					...prev,
					closestStopIndex: closest.index,
				}));
			}

			// Update distance to current target
			const targetIdGuide = ts.targetStopId;
			if (targetIdGuide) {
				const targetDist = distances.find((d) => d.stopId === targetIdGuide);
				if (targetDist) {
					setTrackerState((prev) => ({
						...prev,
						distanceToTarget: targetDist.distance,
					}));
				}
			}

			// Check arrival at any unvisited stop
			for (const dist of distances) {
				if (visitedLocationsRef.current[dist.stopId]) continue;

				if (isWithinArrivalThreshold(dist.distance, ARRIVAL_THRESHOLD_METERS)) {
					handleGuideRouteArrival(dist.stopId, dist.index);
					return;
				}
			}

			// Check for skipped stops
			const currentIdxGuide = ts.currentStopIndex;
			if (closest && closest.index > currentIdxGuide) {
				for (let i = currentIdxGuide; i < closest.index; i++) {
					const skippedId = gr.stopOrder[i];
					if (skippedId && !visitedLocationsRef.current[skippedId]) {
						handleSkippedStop(skippedId, i);
					}
				}
			}

			// Off-route check using polylineCoords from guide route segments
			const currentSegment = gr.segments.find(
				(seg) => seg.fromStopId === targetIdGuide || seg.toStopId === targetIdGuide
			);
			if (currentSegment?.polylineCoords && currentSegment.polylineCoords.length > 0) {
				if (isOffRouteFromCoords(userLat, userLng, currentSegment.polylineCoords)) {
					handleOffRoute();
				}
			}
		}
	}, [
		getActiveStopOrder, getStopsWithCoords,
		handleArrival, handleMultiStopArrival, handleGuideRouteArrival,
		handleOffRoute, handleSkippedStop,
		setTrackerState, tourState, routeToStop,
	]);

	// ---- Auto-disable when route is cleared ----
	useEffect(() => {
		if (!navigatorEnabled) return;

		const routeType = trackerState.routeType;
		if (routeType === 'single-stop' && !routeToStop) {
			disableNavigator();
		}
		if (routeType === 'guide-route' && !guideRoute) {
			disableNavigator();
		}
	}, [navigatorEnabled, trackerState.routeType, routeToStop, guideRoute, disableNavigator]);

	// ---- Auto-activate nav mode the moment a single-stop route arrives ----
	// Only routeToStopLocation represents explicit user intent ("I want directions").
	// guideRoute is pre-authored tour data — must NOT auto-trigger.
	const prevRouteRef = useRef<boolean>(false);
	useEffect(() => {
		const hasRoute = !!routeToStop;
		if (hasRoute && !prevRouteRef.current && !navigatorEnabled) {
			setNavigatorEnabled(true);
		}
		prevRouteRef.current = hasRoute;
	}, [routeToStop, navigatorEnabled, setNavigatorEnabled]);

	// ---- Sync tracker isActive to nav-mode flag ----
	// When nav mode turns on (by any path — auto, manual toggle, etc.) and a route exists,
	// initialize the tracker. When nav mode turns off, reset.
	useEffect(() => {
		const hasRoute = !!(routeToStop || (guideRoute?.stopOrder && guideRoute.segments?.length));
		if (navigatorEnabled && hasRoute && !trackerState.isActive) {
			enableNavigator();
		} else if (!navigatorEnabled && trackerState.isActive) {
			disableNavigator();
		}
	}, [navigatorEnabled, routeToStop, guideRoute, trackerState.isActive, enableNavigator, disableNavigator]);

	// ---- Fire an immediate progress check when tracking becomes active ----
	// Without this, arrivals only fire on the next GPS update from watchPosition.
	// If the user is already within range when they tap Navigate and the phone is
	// stationary, no update comes and no toast shows.
	const prevActiveRef = useRef(false);
	useEffect(() => {
		if (trackerState.isActive && !prevActiveRef.current) {
			const geo = getGeolocation();
			if (geo?.latitude != null && geo?.longitude != null) {
				checkRouteProgress({ lat: geo.latitude, lng: geo.longitude });
			}
		}
		prevActiveRef.current = trackerState.isActive;
	}, [trackerState.isActive, getGeolocation, checkRouteProgress]);

	// ---- Computed values ----

	const stopOrder = useMemo(() => {
		if (trackerState.routeType === 'guide-route') {
			return guideRoute?.stopOrder ?? [];
		}
		return getActiveStopOrder() ?? [];
	}, [trackerState.routeType, guideRoute, getActiveStopOrder]);

	const visitedCount = useMemo(() => {
		return stopOrder.filter((id) => visitedLocations[id]).length;
	}, [stopOrder, visitedLocations]);

	const totalStops = stopOrder.length;

	const formattedDistance = useMemo(() => {
		if (trackerState.distanceToTarget === null) return null;
		return formatDistance(trackerState.distanceToTarget);
	}, [trackerState.distanceToTarget]);

	const targetStopInfo = useMemo((): StopInfo | null => {
		if (!trackerState.targetStopId) return null;
		return getStopInfo(trackerState.targetStopId, trackerState.currentStopIndex);
	}, [trackerState.targetStopId, trackerState.currentStopIndex, getStopInfo]);

	const upcomingStops = useMemo((): StopInfo[] => {
		return stopOrder
			.map((id, index) => getStopInfo(id, index))
			.filter((info): info is StopInfo => info !== null && !info.visited && !info.skipped)
			.filter((info) => info.stopId !== trackerState.targetStopId);
	}, [stopOrder, getStopInfo, trackerState.targetStopId]);

	const skippedStops = useMemo((): StopInfo[] => {
		return trackerState.skippedStopIds
			.map((id) => {
				const index = stopOrder.indexOf(id);
				return getStopInfo(id, index >= 0 ? index : 0);
			})
			.filter((info): info is StopInfo => info !== null);
	}, [trackerState.skippedStopIds, stopOrder, getStopInfo]);

	const visitedStops = useMemo((): StopInfo[] => {
		return stopOrder
			.map((id, index) => getStopInfo(id, index))
			.filter((info): info is StopInfo => info !== null && info.visited);
	}, [stopOrder, getStopInfo]);

	const hasActiveRoute = useMemo(() => {
		return !!(
			routeToStop ||
			(guideRoute?.stopOrder && guideRoute.stopOrder.length > 0 && guideRoute.segments.length > 0)
		);
	}, [routeToStop, guideRoute]);

	// ---- Return ----

	return {
		// State
		isActive: trackerState.isActive,
		isNavigatorEnabled: navigatorEnabled,
		hasActiveRoute,
		routeType: trackerState.routeType,
		status: trackerState.status,
		targetStopId: trackerState.targetStopId,
		distanceToTarget: trackerState.distanceToTarget,
		currentStopIndex: trackerState.currentStopIndex,
		closestStopIndex: trackerState.closestStopIndex,
		navigatorEnabled,

		// Actions
		enableNavigator,
		disableNavigator,
		toggleNavigator,
		resetTracker,
		checkRouteProgress,

		// Computed
		formattedDistance,
		visitedCount,
		totalStops,
		targetStopInfo,
		upcomingStops,
		skippedStops,
		visitedStops,
		stopOrder,
	};
}
